/* eslint-disable camelcase */
import React, { useEffect, useState } from 'react';
import { Box, Dialog, Divider, Grid, IconButton, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import { CancelPresentationRounded, ChevronLeft, ChevronRight } from '@mui/icons-material';
import columns from './table_config';
import styles from './styles';
import Datagrid from '../../../../ui/Datagrid';
import CanvasJSReact from '../../../../ui/canvasjs/canvasjs.react';
import { isMobile } from 'react-device-detect';
import { COLORS } from '../../../../../shared/config/colors';

const CanvasJSChart = CanvasJSReact.CanvasJSChart;

const PumpRampChart = ({
  onCloseClick,
  isOpen,
  pumpRamps,
  pumpRampUnits
}) => {
  const classes = styles();
  const [tableRow, setTableRows] = useState([]);
  const [showTable, setShowTable] = useState(false);
  const [showMobileChartTable, setShowMobileChartTable] = useState(false);
  const [hasNoCuttings, setHasNoCuttings] = useState(false);
  const [targetECDWithSBP, setTargetECDWithSBP] = useState(false);

  useEffect(() => {
    const records = pumpRamps?.results?.pump_ramps?.map((record, index) => ({
      ...record,
      sbp: Number(record?.sbp?.toFixed(0)),
      no_cuttings_sbp: record?.no_cuttings_sbp ?? null,
      target_name: record?.target_name ?? null,
      target_ecd_with_sbp: record?.target_ecd_with_sbp ?? null,
      _id: Math.random().toString(36).substring(2, 10),
      steps: index + 1,
    }));
    const hasCuttings = records.some(
      (record) => record?.no_cuttings_sbp !== null && record?.no_cuttings_sbp !== undefined
    );
    const hasTargetECDWithSBP = records.some(
      (record) => record?.target_ecd_with_sbp !== null && record?.target_ecd_with_sbp !== undefined
    );

    setHasNoCuttings(hasCuttings);
    setTargetECDWithSBP(hasTargetECDWithSBP);
    setTableRows(records);
    setShowTable(true);
  }, [pumpRamps]);

  const targetName = tableRow?.find((row) => row?.target_name)?.target_name;

  const options = {
    height: 450,
    animationEnabled: true,
    theme: 'light2',
    axisY: {
      title: `SBP (${pumpRampUnits?.sbp?.label})`,
      includeZero: true,
    },
    axisY3: {
      title: `Clean-Mud SBP (${pumpRampUnits?.no_cuttings_sbp?.label})`,
      includeZero: true,
    },
    axisY2: {
      title: `Flow In (${pumpRampUnits?.flow_in?.label})`,
    },
    axisX: {
      title: 'Steps',
    },
    data: [
      {
        type: 'column',
        showInLegend: true,
        name: `Flow In (${pumpRampUnits?.flow_in?.label})`,
        color: '#c0d0e5',
        toolTipContent: `Flow In: {y} ${pumpRampUnits?.flow_in?.label}`,
        axisYType: 'secondary',
        dataPoints: pumpRamps?.results?.pump_ramps.map((dataPoint) => ({
          label: 'Flow In',
          y: dataPoint.flow_in,
        })),
      },
      {
        type: 'line',
        name: `SBP (${pumpRampUnits?.sbp?.label})`,
        showInLegend: true,
        color: '#1D3956',
        axisYType: 'primary',
        toolTipContent: `SBP: {y} ${pumpRampUnits?.sbp?.label}`,
        dataPoints: pumpRamps?.results?.pump_ramps.map((dataPoint, index) => ({
          label: `${index + 1}`,
          y: dataPoint.sbp,
        })),
      },
      {
        type: 'line',
        name: `Clean-Mud SBP (${pumpRampUnits?.no_cuttings_sbp?.label})`,
        showInLegend: hasNoCuttings,
        color: COLORS.ROYAL_BLUE,
        axisYType: 'primary',
        toolTipContent: `Clean-Mud SBP: {y} ${pumpRampUnits?.no_cuttings_sbp?.label}`,
        dataPoints: pumpRamps?.results?.pump_ramps.map((dataPoint, index) => ({
          label: `${index + 1}`,
          y: dataPoint.no_cuttings_sbp,
        })),
      },
    ],
  };

  const WebRampChartTableComponent = () => {
    return (
      <>
        <Grid container item xs={showTable ? 8 : 12} alignItems="center" justifyContent="center">
          <Grid item style={{ fontWeight: 'bold', fontSize: '1.3rem', marginTop: showTable ? '3%' : '2.5%' }}>
            {'Ramp Schedule'}
          </Grid>
        </Grid>
        <Box style={{
          overflow: 'auto',
          marginTop: '-4%'
        }}>
          <Grid container marginBottom={'2%'}>
            <Grid item xs={showTable ? 8 : 12} sx={{
              p: 2,
              pt: 10,
            }}>
              <CanvasJSChart options={options} />
            </Grid>
            {!showTable && <Box className={classes.menuTogglerBtn}>
              <IconButton
                onClick={() => {
                  setShowTable(!showTable);
                }}>
                <ChevronLeft sx={{ fontSize: '33px' }} />
              </IconButton>
            </Box>}
            {showTable && <Grid item xs={4} marginTop={'2.5%'}>
              <Box className={classes.menuTogglerTableBtn}>
                <IconButton
                  onClick={() => {
                    setShowTable(!showTable);
                  }}>
                  <ChevronRight sx={{ fontSize: '33px' }} />
                </IconButton>
              </Box>
              {showTable && <Grid item xs={12}>
                <Datagrid
                  loading={false}
                  data={tableRow}
                  sx={{
                    '.MuiDataGrid-cell': {
                      fontSize: '1rem',
                      padding: '2px',
                    },
                    '.MuiDataGrid-columnHeader': {
                      fontSize: '1.2rem',
                      fontWeight: '900 !important',
                    },
                    '& .MuiDataGrid-columnHeaderTitle': {
                      overflow: 'visible',
                      lineHeight: '1.43rem',
                      whiteSpace: 'normal',
                    },
                    '& .MuiDataGrid-footerContainer': {
                      minHeight: '0px'
                    },
                  }}
                  columns={columns(pumpRampUnits, hasNoCuttings, targetECDWithSBP, targetName)}
                  disableStickyHeader={true}
                  autoHeight={false}
                  hideFooterPagination
                  hideFooterSelectedRowCount
                  height={'500px'}
                  getRowHeight={() => 'auto'}
                  pageSize={100}
                  toolbar={{
                    options: {
                      columns: false,
                      filters: false,
                      density: false,
                      export: {
                        enabled: false,
                      }
                    },
                  }}
                />
              </Grid>}
            </Grid>}
          </Grid>
        </Box>
      </>
    );
  };

  const MobileRampChartTableComponent = () => {
    return (
      <Grid sx={{ overflowY: 'auto' }}>
        {!showMobileChartTable
          ? (
            <>
              <Grid sx={{
                pt: 10,
              }}>
                <CanvasJSChart options={options} />
              </Grid>
              <Box className={classes.menuTogglerMobileBtn}>
                <IconButton
                  onClick={() => {
                    setShowMobileChartTable(true);
                  }}>
                  <ChevronLeft sx={{ fontSize: '33px' }} />
                </IconButton>
              </Box>
            </>
            )
          : (
            <Grid item xs={12}>
              {tableRow.map((row, index) => (
                <Box key={index} className={classes.gridContainer}>
                  {columns(pumpRampUnits, hasNoCuttings, targetECDWithSBP, targetName)
                    ?.filter((col) => !col.hide)
                    .map((col, idx) => {
                      const value = col?.valueGetter
                        ? col.valueGetter({ row })
                        : col?.valueFormatter
                          ? col.valueFormatter({
                            row,
                            field: col.field,
                            value: row[col.field],
                          })
                          : row[col.field];

                      return (
                        <Grid container key={idx} className={classes.gridItem}>
                          <Typography className={classes.headerName} variant="body1">
                            {col.headerName}
                          </Typography>
                          <Typography sx={{ flex: 0.5 }} variant="body1">
                            {value}
                          </Typography>
                        </Grid>
                      );
                    })}
                </Box>
              ))}

              <Box className={classes.menuTogglerTableMobileBtn}>
                <IconButton
                  onClick={() => {
                    setShowMobileChartTable(false);
                  }}>
                  <ChevronRight sx={{ fontSize: '33px' }} />
                </IconButton>
              </Box>
            </Grid>
            )}
      </Grid>
    );
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onCloseClick}
      maxWidth={'lg'}
      fullScreen={isMobile}
      fullWidth={true}
      style={{
        marginRight: !isMobile && '20px'
      }}
      PaperProps={{
        sx: {
          overflow: 'visible'
        },
      }}
    >
      <Box className={classes.dialogTitle}>
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', fontSize: '1.3rem' }}>
          <Grid container alignItems="center" justifyContent={isMobile ? 'space-between' : 'flex-end'}>
            {isMobile
              ? (
                <Grid item>
                  <Typography variant='h6' fontWeight='bold'>
                    {'Ramp Schedule'}
                  </Typography>
                </Grid>
                )
              : null}
            <Grid item>
              <IconButton
                onClick={() => {
                  onCloseClick();
                }}
              >
                <CancelPresentationRounded fontSize='large' />
              </IconButton>
            </Grid>
          </Grid>
        </Typography>
      </Box>
      <Divider />
      {isMobile
        ? (
          <MobileRampChartTableComponent />
          )
        : (
          <WebRampChartTableComponent />
          )}
    </Dialog>
  );
};
PumpRampChart.propTypes = {
  onCloseClick: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
  pumpRamps: PropTypes.any,
  pumpRampUnits: PropTypes.any
};

export default PumpRampChart;
